/**
 * Twenty-two colours, chosen to stay apart from each other.
 *
 * They run around the wheel rather than through one family, because the job
 * these colours do — telling one group or tag from another at a glance — fails
 * the moment two of them are close. Every one is dark enough for white text on
 * top and light enough to tint a chip background; the last four are deeper
 * tones for when the bright ones are used up.
 *
 * Twenty-two and not sixteen because eleven fit on a row in the dialog: two
 * full rows instead of one full and one with five swatches rattling in it.
 *
 * The brand orange is first: it is the default for a new thing, and defaults
 * belong where the eye starts.
 */
export const PALETTE = [
  '#FF6500', // brand orange
  '#F97316', // orange
  '#F59E0B', // amber
  '#EAB308', // yellow
  '#84CC16', // lime
  '#22C55E', // green
  '#10B981', // emerald
  '#14B8A6', // teal
  '#06B6D4', // cyan
  '#0EA5E9', // sky
  '#3B82F6', // blue
  '#6366F1', // indigo
  '#8B5CF6', // violet
  '#A855F7', // purple
  '#D946EF', // fuchsia
  '#EC4899', // pink
  '#F43F5E', // rose
  '#EF4444', // red
  '#B45309', // deep amber
  '#0F766E', // deep teal
  '#1E40AF', // navy
  '#64748B', // slate
] as const

export type PaletteColor = (typeof PALETTE)[number]

/**
 * A colour for a tag that has none, derived from its name.
 *
 * Deterministic on purpose: the same tag gets the same colour on every screen
 * and after every reload, so a community that never opens the picker still
 * reads as something arranged rather than something random.
 */
export const colorForName = (name: string): string => {
  let hash = 0
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.toLowerCase().charCodeAt(i)) >>> 0
  }
  return PALETTE[hash % PALETTE.length]
}

/**
 * How a coloured chip is drawn: the colour for the text, a wash of it behind.
 *
 * The wash is the same colour at 10% rather than a second value, so a tag can
 * never end up with a border, a background and a label that disagree — and so
 * the twenty-two colours stay legible on white without a second palette of
 * pastels to maintain.
 */
export const chipStyle = (color: string | null | undefined, name: string) => {
  const hue = color || colorForName(name)
  return { color: hue, backgroundColor: `${hue}1a`, borderColor: `${hue}33` }
}
